import { z } from 'zod';

// ---------------------------------------------------------------------------
// Org export branding: header/footer, logo, fonts and heading styles applied
// to HTML / PDF / DOCX exports. See docs/CONTRACTS.md.
// ---------------------------------------------------------------------------

/**
 * Font choices are a safe fixed set so every export format can honour them:
 * css stack (HTML), pdf-lib standard font (PDF) and font name (DOCX).
 */
export const BRANDING_FONTS = [
  {
    key: 'georgia',
    label: 'Georgia (serif)',
    css: "Georgia, 'Times New Roman', serif",
    pdf: 'TimesRoman',
    docx: 'Georgia',
  },
  {
    key: 'times',
    label: 'Times New Roman (serif)',
    css: "'Times New Roman', Times, serif",
    pdf: 'TimesRoman',
    docx: 'Times New Roman',
  },
  {
    key: 'inter',
    label: 'Inter / system sans-serif',
    css: "Inter, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
    pdf: 'Helvetica',
    docx: 'Calibri',
  },
  {
    key: 'arial',
    label: 'Arial (sans-serif)',
    css: 'Arial, Helvetica, sans-serif',
    pdf: 'Helvetica',
    docx: 'Arial',
  },
  {
    key: 'courier',
    label: 'Courier (monospace)',
    css: "'Courier New', Courier, monospace",
    pdf: 'Courier',
    docx: 'Courier New',
  },
] as const;

export const BrandingFontSchema = z.enum(['georgia', 'times', 'inter', 'arial', 'courier']);
export type BrandingFont = z.infer<typeof BrandingFontSchema>;

export const HeadingStyleSchema = z.object({
  sizePt: z.number().int().min(8).max(72),
  fontFamily: BrandingFontSchema,
  /** #rrggbb */
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export type HeadingStyle = z.infer<typeof HeadingStyleSchema>;

export const BrandingConfigSchema = z.object({
  /** Shown at the top of every exported document (plain text). */
  headerText: z.string().max(300).default(''),
  /** Shown at the end of every exported document (plain text). */
  footerText: z.string().max(300).default(''),
  /** Body font. */
  fontFamily: BrandingFontSchema.default('georgia'),
  headings: z
    .object({
      h1: HeadingStyleSchema.default({ sizePt: 24, fontFamily: 'georgia', color: '#1a202c' }),
      h2: HeadingStyleSchema.default({ sizePt: 18, fontFamily: 'georgia', color: '#1a202c' }),
      h3: HeadingStyleSchema.default({ sizePt: 14, fontFamily: 'georgia', color: '#1a202c' }),
    })
    .default({
      h1: { sizePt: 24, fontFamily: 'georgia', color: '#1a202c' },
      h2: { sizePt: 18, fontFamily: 'georgia', color: '#1a202c' },
      h3: { sizePt: 14, fontFamily: 'georgia', color: '#1a202c' },
    }),
});
export type BrandingConfig = z.infer<typeof BrandingConfigSchema>;

export const DEFAULT_BRANDING: BrandingConfig = BrandingConfigSchema.parse({});

export const OrgBrandingSchema = z.object({
  orgId: z.string(),
  config: BrandingConfigSchema,
  hasLogo: z.boolean(),
  logoContentType: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type OrgBranding = z.infer<typeof OrgBrandingSchema>;

export const UpdateOrgBrandingInputSchema = BrandingConfigSchema;
export type UpdateOrgBrandingInput = z.infer<typeof UpdateOrgBrandingInputSchema>;
